import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'react-hot-toast';
import Modal from '../components/ui/Modal';
  import {
    User,
    Mail,
    Phone,
    Building2,
    Shield,
    Calendar,
    Camera,
    Save,
    Upload,
    X,
    ZoomIn,
    ZoomOut,
    RotateCw,
    Crop,
    MapPin,
    Map,
    Download,
    FileArchive,
    Loader2,
    CheckCircle,
    Trash2
  } from 'lucide-react';
import apiService from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import PhoneInput from '../components/forms/PhoneInput';
import StateSelect from '../components/forms/StateSelect';
import { formatDate, getProfilePhotoUrl } from '../utils/formatters';

interface UserProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  roleName: string;
  companyName: string;
  profilePhotoUrl?: string;
  createdAt: string;
  lastLoginAt?: string;
  isActive: boolean;
}

interface UpdateProfileData {
  email: string;
  phone?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

const UserProfilePage: React.FC = () => {
  const { user, updateUserProfilePhoto } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<UpdateProfileData>({
    email: '',
    phone: '',
    city: '',
    state: '',
    zipCode: ''
  });
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImage, setCropImage] = useState<string>('');
  const [cropZoom, setCropZoom] = useState(1);
  const [cropRotation, setCropRotation] = useState(0);
  const [cropPosition, setCropPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  
  // Export state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportStatus, setExportStatus] = useState<'idle' | 'exporting' | 'success'>('idle');
  const [exportProgress, setExportProgress] = useState(0);
  const [exportToken, setExportToken] = useState<string | null>(null);
  
  interface ExportDownloadInfo {
    token: string;
    createdAt: string;
    expiresAt: string;
    downloadedAt: string | null;
    isExpired: boolean;
    fileName: string;
    fileSize: number;
  }
  
  const [previousExports, setPreviousExports] = useState<ExportDownloadInfo[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [exportToDelete, setExportToDelete] = useState<string | null>(null);

  // Fetch user profile data
  const { data: profileResponse, isLoading } = useQuery({
    queryKey: ['user-profile'],
    queryFn: () => apiService.getUserProfile(),
    enabled: !!user
  });

  const profile: UserProfile | null = profileResponse?.data || null;

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data: UpdateProfileData) => apiService.updateUserProfile(data),
    onSuccess: () => {
      toast.success('Profile updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    }
  });

  // Upload photo mutation
  const uploadPhotoMutation = useMutation({
    mutationFn: (file: File) => apiService.uploadUserProfilePhoto(file),
    onSuccess: (response) => {
      toast.success('Profile photo updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      queryClient.invalidateQueries({ queryKey: ['user'] }); // Update auth context
      
      // Update the user's profile photo in auth context
      // The API returns just the URL string, not an object
      if (response.data) {
        updateUserProfilePhoto(response.data);
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to upload photo');
    }
  });

  React.useEffect(() => {
    if (profile) {
      setFormData({
        email: profile.email,
        phone: profile.phone || '',
        city: profile.city || '',
        state: profile.state || '',
        zipCode: profile.zipCode || ''
      });
    }
  }, [profile]);

  const handleSave = () => {
    updateProfileMutation.mutate(formData);
  };

  const handleInputChange = (field: keyof UpdateProfileData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateAndUpload = (file?: File) => {
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      // Open crop modal instead of direct upload
      const reader = new FileReader();
      reader.onload = (e) => {
        setCropImage(e.target?.result as string);
        setShowCropModal(true);
        setCropZoom(1);
        setCropRotation(0);
        setCropPosition({ x: 0, y: 0 });
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    validateAndUpload(file);
  };

  const handleDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    validateAndUpload(file);
  };

  const handleDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handlePhotoClick = () => {
    if (profile?.profilePhotoUrl) {
      setShowPhotoModal(true);
    } else {
      fileInputRef.current?.click();
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role.toLowerCase()) {
      case 'superadmin': return 'bg-red-100 text-red-800';
      case 'admin': return 'bg-accent-color-100 text-accent-color-800';
      case 'user': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Cropping functions
  const handleZoomIn = () => {
    setCropZoom(prev => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    setCropZoom(prev => Math.max(prev - 0.1, 0.5));
  };

  const handleRotate = () => {
    setCropRotation(prev => (prev + 90) % 360);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - cropPosition.x, y: e.clientY - cropPosition.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setCropPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const cropAndUpload = useCallback(() => {
    if (!canvasRef.current || !imageRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = imageRef.current;

    if (!ctx) return;

    // Set canvas size to 200x200 (profile photo size)
    canvas.width = 200;
    canvas.height = 200;

    // Clear canvas
    ctx.clearRect(0, 0, 200, 200);

    // Calculate the crop area based on the circular overlay
    const cropSize = 200; // The size of our circular crop area
    const containerSize = 400; // The size of our crop container
    
    // Image CSS dimensions are set to 400 * cropZoom with objectFit: cover
    // With objectFit cover, the image scales to fill while maintaining aspect ratio
    const cssDisplaySize = 400 * cropZoom;
    
    // Calculate how objectFit: cover actually displays the image
    // The scale factor determines how the image fills the container
    // scaleForCover = max(cssDisplaySize/naturalWidth, cssDisplaySize/naturalHeight)
    // This ensures the image covers the entire container
    const scaleForWidth = cssDisplaySize / img.naturalWidth;
    const scaleForHeight = cssDisplaySize / img.naturalHeight;
    const scaleForCover = Math.max(scaleForWidth, scaleForHeight);
    
    // The actual displayed image dimensions (what's rendered)
    const displayedWidth = img.naturalWidth * scaleForCover;
    const displayedHeight = img.naturalHeight * scaleForCover;
    
    // Calculate reverse scale: natural pixels per displayed pixel
    // This is the inverse of scaleForCover
    const scale = 1 / scaleForCover;
    
    // Center of container (where crop overlay circle is centered)
    const containerCenterX = containerSize / 2;
    const containerCenterY = containerSize / 2;
    
    // The image is centered in the container with CSS: top-1/2 left-1/2 transform -50%
    // Then offset by cropPosition.x and cropPosition.y
    // Image center in container coordinates
    const imageCenterInContainerX = containerCenterX + cropPosition.x;
    const imageCenterInContainerY = containerCenterY + cropPosition.y;
    
    // Crop center is at container center (200, 200) in container coordinates
    // Convert crop center to coordinates relative to image top-left
    // Image top-left = image center - half displayed size
    const imageTopLeftX = imageCenterInContainerX - (displayedWidth / 2);
    const imageTopLeftY = imageCenterInContainerY - (displayedHeight / 2);
    
    // Crop center relative to image top-left
    const cropCenterX = containerCenterX - imageTopLeftX;
    const cropCenterY = containerCenterY - imageTopLeftY;
    
    // Top-left of crop area relative to image top-left
    const cropX = cropCenterX - cropSize / 2;
    const cropY = cropCenterY - cropSize / 2;
    
    // Convert to natural image coordinates
    const sourceX = cropX * scale;
    const sourceY = cropY * scale;
    const sourceSize = cropSize * scale;

    // Apply rotation
    ctx.save();
    ctx.translate(100, 100);
    ctx.rotate((cropRotation * Math.PI) / 180);
    ctx.translate(-100, -100);

    // Draw the cropped and rotated image
    ctx.drawImage(
      img,
      sourceX,
      sourceY,
      sourceSize,
      sourceSize,
      0,
      0,
      cropSize,
      cropSize
    );

    ctx.restore();

    // Convert to blob and upload
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], 'profile-photo.jpg', { type: 'image/jpeg' });
        uploadPhotoMutation.mutate(file);
        setShowCropModal(false);
      }
    }, 'image/jpeg', 0.9);
  }, [cropZoom, cropRotation, cropPosition, uploadPhotoMutation]);

  // Export function with progress
  const handleExport = async () => {
    setShowExportModal(true);
    setExportStatus('exporting');
    setExportProgress(0);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setExportProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await apiService.post('/companies/export');
      clearInterval(progressInterval);
      setExportProgress(100);
      
      console.log('Export response:', response);
      console.log('Response data:', response.data);
      
      // The backend now returns the token directly: { success: true, data: "TOKEN_STRING" }
      const token = response.data;
      console.log('Export token:', token);
      
      if (token && typeof token === 'string') {
        // Use the token-based API endpoint (it will redirect to static file)
        const baseUrl = (process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:5269`).replace('/api', '');
        const fullDownloadUrl = `${baseUrl}/api/companies/export/download/${token}`;
        console.log('Full download URL:', fullDownloadUrl);
        setExportToken(fullDownloadUrl);
        setExportStatus('success');
      } else {
        console.error('No valid token received in response:', response);
        throw new Error('No export token received');
      }
    } catch (error: any) {
      console.error('Export error:', error);
      setExportStatus('idle');
      toast.error(error.response?.data?.message || error.message || 'Failed to create export');
      setShowExportModal(false);
    }
  };

  const downloadExport = () => {
    if (exportToken) {
      // exportToken now contains the full URL
      window.open(exportToken, '_blank');
    }
  };
  
  const fetchPreviousExports = async () => {
    try {
      const response = await apiService.get('/companies/exports');
      if (response.data) {
        setPreviousExports(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch previous exports:', error);
    }
  };
  
  const downloadPreviousExport = (token: string) => {
    const baseUrl = (process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:5269`).replace('/api', '');
    const downloadUrl = `${baseUrl}/api/companies/export/download/${token}`;
    window.open(downloadUrl, '_blank');
  };
  
  const handleDeleteClick = (token: string) => {
    setExportToDelete(token);
    setShowDeleteConfirm(true);
  };
  
  const deleteExport = async () => {
    if (!exportToDelete) return;
    
    try {
      await apiService.delete(`/companies/exports/${exportToDelete}`);
      toast.success('Export deleted successfully');
      fetchPreviousExports();
      setShowDeleteConfirm(false);
      setExportToDelete(null);
    } catch (error: any) {
      console.error('Failed to delete export:', error);
      toast.error(error.response?.data?.message || 'Failed to delete export');
    }
  };
  
  // Fetch previous exports on mount and after successful export
  React.useEffect(() => {
    fetchPreviousExports();
  }, []);
  
  React.useEffect(() => {
    if (exportStatus === 'success') {
      fetchPreviousExports();
    }
  }, [exportStatus]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <div className="h-64 bg-gray-200 rounded-lg"></div>
            </div>
            <div className="lg:col-span-2">
              <div className="space-y-4">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Profile Not Found</h3>
        <p className="text-gray-500">Unable to load your profile information.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
            <User className="h-6 w-6 mr-2" />
            My Profile
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Manage your personal information and settings</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Photo Section */}
        <div className="lg:col-span-1">
          <Card className="p-6">
            <div className="text-center space-y-4">
              {/* Current Photo Preview */}
              {profile.profilePhotoUrl && (
                <div className="flex justify-center">
                  <div className="h-24 w-24 rounded-full border-4 border-accent-color">
                    <img
                      src={getProfilePhotoUrl(profile.profilePhotoUrl)}
                      alt={`${profile.firstName} ${profile.lastName}`}
                      className="h-full w-full object-cover rounded-full cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => setShowPhotoModal(true)}
                      title="Click to view larger"
                    />
                  </div>
                </div>
              )}

              {/* Drag and Drop Upload Area */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  isDragOver
                    ? 'border-accent-color bg-accent-color-50'
                    : 'border-gray-300 hover:border-gray-400'
                } ${uploadPhotoMutation.isPending ? 'opacity-50 pointer-events-none' : ''}`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif"
                  onChange={handlePhotoUpload}
                  disabled={uploadPhotoMutation.isPending}
                  className="hidden"
                  id="profile-photo-upload"
                />
                <div className="space-y-2">
                  <div className="mx-auto w-12 h-12 text-gray-400">
                    {uploadPhotoMutation.isPending ? (
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-color"></div>
                    ) : (
                      <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    )}
                  </div>
                  <div className="text-sm">
                    {uploadPhotoMutation.isPending ? (
                      <p className="text-accent-color font-medium">Uploading...</p>
                    ) : (
                      <>
                        <p className="text-gray-600">
                          <span className="font-medium text-accent-color">Click to upload</span> or drag and drop
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          JPEG, PNG, GIF up to 5MB
                        </p>
                      </>
                    )}
                  </div>
                  <label
                    htmlFor="profile-photo-upload"
                    className="btn-primary cursor-pointer"
                  >
                    {profile.profilePhotoUrl ? 'Choose New Photo' : 'Choose Photo'}
                  </label>
                </div>
              </div>
              
              <h3 className="text-lg font-medium text-gray-900">
                {profile.firstName} {profile.lastName}
              </h3>
              
              <Badge className={`${getRoleBadgeColor(profile.roleName)}`}>
                {profile.roleName}
              </Badge>
            </div>
          </Card>
        </div>

        {/* Profile Information */}
        <div className="lg:col-span-2">
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">Profile Information</h3>
            
            <div className="space-y-6">
              {/* Immutable Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <User className="h-4 w-4 inline mr-2" />
                    First Name
                  </label>
                  <Input
                    value={profile.firstName}
                    disabled
                    className="bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <User className="h-4 w-4 inline mr-2" />
                    Last Name
                  </label>
                  <Input
                    value={profile.lastName}
                    disabled
                    className="bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <Shield className="h-4 w-4 inline mr-2" />
                    Role
                  </label>
                  <Input
                    value={profile.roleName}
                    disabled
                    className="bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <Building2 className="h-4 w-4 inline mr-2" />
                    Company
                  </label>
                  <Input
                    value={profile.companyName}
                    disabled
                    className="bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
              </div>

              {/* Editable Fields */}
              <div className="border-t pt-6">
                <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Contact Information</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Mail className="h-4 w-4 inline mr-2" />
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      type="email"
                      placeholder="Enter your email"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Phone className="h-4 w-4 inline mr-2" />
                      Phone Number
                    </label>
                    <PhoneInput
                      value={formData.phone}
                      onChange={(value) => handleInputChange('phone', value)}
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div className="border-t pt-6">
                <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Address Information</h4>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <MapPin className="h-4 w-4 inline mr-2" />
                      City
                    </label>
                    <Input
                      value={formData.city || ''}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      placeholder="Enter city"
                      autoComplete="address-level2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Map className="h-4 w-4 inline mr-2" />
                      State
                    </label>
                    <StateSelect
                      value={formData.state || ''}
                      onChange={(value) => handleInputChange('state', value)}
                      placeholder="Select state"
                      autoComplete="address-level1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <MapPin className="h-4 w-4 inline mr-2" />
                      Zip Code
                    </label>
                    <Input
                      value={formData.zipCode || ''}
                      onChange={(e) => handleInputChange('zipCode', e.target.value)}
                      placeholder="Enter zip code"
                      maxLength={10}
                      autoComplete="postal-code"
                    />
                  </div>
                </div>
              </div>

              {/* Account Information */}
              <div className="border-t pt-6">
                <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Account Information</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Calendar className="h-4 w-4 inline mr-2" />
                      Member Since
                    </label>
                    <Input
                      value={formatDate(profile.createdAt)}
                      disabled
                      className="bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Calendar className="h-4 w-4 inline mr-2" />
                      Last Login
                    </label>
                    <Input
                      value={profile.lastLoginAt ? formatDate(profile.lastLoginAt) : 'Never'}
                      disabled
                      className="bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                </div>
              </div>

              {/* Theme Settings */}
              <div className="border-t pt-6">
                <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Appearance</h4>
                
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <h5 className="text-sm font-medium text-gray-900 dark:text-gray-100">Theme</h5>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Choose your preferred color scheme
                    </p>
                  </div>
                  <button
                    onClick={toggleTheme}
                    className="flex items-center space-x-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                  >
                    <span className="text-lg">
                      {isDark ? '🌙' : '☀️'}
                    </span>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {isDark ? 'Dark' : 'Light'}
                    </span>
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="border-t pt-6 flex justify-end">
                <Button
                  onClick={handleSave}
                  disabled={updateProfileMutation.isPending || !formData.email.trim()}
                >
                  {updateProfileMutation.isPending ? (
                    <>
                      <Save className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Data Export Section */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Export Your Data</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Download all your company data including work orders, clients, invoices, and uploaded files
            </p>
          </div>
          <Button onClick={handleExport}>
            Export All Data
          </Button>
        </div>
        
        {/* Previous Exports */}
        {previousExports.length > 0 && (
          <div className="border-t pt-6">
            <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Previous Exports</h4>
            <div className="space-y-2">
              {previousExports.map((exportItem) => (
                <div
                  key={exportItem.token}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <FileArchive className="h-5 w-5 text-accent-color" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {exportItem.fileName}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(exportItem.createdAt).toLocaleString()} • 
                        {(exportItem.fileSize / 1024 / 1024).toFixed(2)} MB
                        {exportItem.isExpired && ' • Expired'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => downloadPreviousExport(exportItem.token)}
                      disabled={exportItem.isExpired}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteClick(exportItem.token)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Photo Preview Modal */}
      <Modal
        isOpen={showPhotoModal && !!profile.profilePhotoUrl}
        onClose={() => setShowPhotoModal(false)}
        title="Profile Photo"
        maxWidth="md"
      >
        <div className="text-center">
          <div className="inline-block rounded-full border-4 border-accent-color">
            <img
              src={getProfilePhotoUrl(profile.profilePhotoUrl)}
              alt={`${profile.firstName} ${profile.lastName}`}
              className="w-48 h-48 object-cover rounded-full"
            />
          </div>
        </div>
      </Modal>

      {/* Photo Crop Modal */}
      <Modal
        isOpen={showCropModal && !!cropImage}
        onClose={() => setShowCropModal(false)}
        title="Crop Profile Photo"
        maxWidth="2xl"
      >
        <div className="space-y-4">
              {/* Controls */}
              <div className="flex items-center justify-center space-x-4">
                <button
                  onClick={handleZoomOut}
                  className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                  title="Zoom Out"
                >
                  <ZoomOut className="h-5 w-5" />
                </button>
                <span className="text-sm text-gray-600 min-w-[60px] text-center">
                  {Math.round(cropZoom * 100)}%
                </span>
                <button
                  onClick={handleZoomIn}
                  className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                  title="Zoom In"
                >
                  <ZoomIn className="h-5 w-5" />
                </button>
                <button
                  onClick={handleRotate}
                  className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                  title="Rotate"
                >
                  <RotateCw className="h-5 w-5" />
                </button>
              </div>

              {/* Crop Area */}
              <div className="relative bg-gray-100 rounded-lg overflow-hidden" style={{ height: '400px' }}>
                <div
                  className="relative w-full h-full cursor-move"
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                >
                  <img
                    ref={imageRef}
                    src={cropImage}
                    alt="Crop preview"
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                    style={{
                      width: `${400 * cropZoom}px`,
                      height: `${400 * cropZoom}px`,
                      transform: `translate(-50%, -50%) translate(${cropPosition.x}px, ${cropPosition.y}px) rotate(${cropRotation}deg)`,
                      objectFit: 'cover'
                    }}
                    onLoad={() => {
                      // Center the image when it loads
                      if (imageRef.current) {
                        const img = imageRef.current;
                        const scaledWidth = img.naturalWidth * cropZoom;
                        const scaledHeight = img.naturalHeight * cropZoom;
                        const centerX = (scaledWidth - 400) / 2;
                        const centerY = (scaledHeight - 400) / 2;
                        setCropPosition({ x: -centerX, y: -centerY });
                      }
                    }}
                  />
                  
                  {/* Crop overlay */}
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 bg-black bg-opacity-50"></div>
                    <div 
                      className="absolute border-2 border-white rounded-full"
                      style={{
                        width: '200px',
                        height: '200px',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="flex items-center justify-center space-x-4">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">Preview</p>
                  <div className="w-16 h-16 rounded-full border-2 border-accent-color p-1">
                    <canvas
                      ref={canvasRef}
                      className="w-full h-full rounded-full object-cover"
                      style={{ display: 'none' }}
                    />
                    <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center">
                      <Crop className="h-6 w-6 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => setShowCropModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={cropAndUpload}
                  disabled={uploadPhotoMutation.isPending}
                  className="bg-accent-color hover:bg-accent-color-600"
                >
                  {uploadPhotoMutation.isPending ? 'Uploading...' : 'Crop & Upload'}
                </Button>
              </div>
        </div>
      </Modal>

      {/* Export Progress Modal */}
      <Modal
        isOpen={showExportModal}
        onClose={() => {
          if (exportStatus === 'success') {
            setShowExportModal(false);
            setExportStatus('idle');
            setExportProgress(0);
            setExportToken(null);
          }
        }}
        title="Export Your Data"
        maxWidth="md"
      >
        <div className="space-y-6 py-4">
          {exportStatus === 'exporting' && (
            <>
              <div className="flex items-center justify-center mb-4">
                <Loader2 className="h-12 w-12 text-accent-color animate-spin" />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Creating your data export...</span>
                  <span className="font-medium">{exportProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div 
                    className="bg-accent-color h-full transition-all duration-300 ease-out"
                    style={{ width: `${exportProgress}%` }}
                  />
                </div>
              </div>
              <p className="text-sm text-gray-500 text-center">
                This may take a minute depending on your data size
              </p>
            </>
          )}

          {exportStatus === 'success' && (
            <>
              <div className="flex items-center justify-center mb-4">
                <CheckCircle className="h-12 w-12 text-green-500" />
              </div>
              <div className="text-center space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Export Complete!</h3>
                <p className="text-sm text-gray-600">
                  Your data has been successfully exported and is ready to download.
                </p>
                <Button
                  onClick={downloadExport}
                  className="w-full bg-accent-color hover:bg-accent-color-600 flex items-center justify-center gap-2"
                >
                  <Download className="h-5 w-5" />
                  Download Export File
                </Button>
                <p className="text-xs text-gray-500">
                  This link will be valid for 7 days
                </p>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setExportToDelete(null);
        }}
        title="Delete Export"
        maxWidth="md"
      >
        <div className="space-y-6 py-4">
          <div className="flex items-center justify-center mb-4">
            <Trash2 className="h-12 w-12 text-red-500" />
          </div>
          <div className="text-center space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Delete Export?</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Are you sure you want to delete this export? This action cannot be undone.
            </p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteConfirm(false);
                setExportToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={deleteExport}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UserProfilePage;
